/**
 * A filter, in the words the reader sees.
 *
 * ONE renderer, because the chip on the bar, the export's About sheet and a
 * refusal all have to name the same filter the same way — a spreadsheet that
 * said "Customer: Acme" while the bar said "Customer is none of Acme" would be
 * two answers to "what was this filtered to", and only one of them is in the
 * file somebody emails on.
 */
import {
  type FilterNode,
  type FilterRule,
  type FilterValue,
  isFilterGroup,
  opLabel,
} from './filter';
import { fieldLabel, filterValueType, type ReportModel } from './model';

/** `previous_month` → `Previous month`, without a second table to maintain. */
const macroLabel = (preset: string) => {
  const words = preset.replace(/_/g, ' ');
  return words.charAt(0).toUpperCase() + words.slice(1);
};

/** How one value reads. Labels are the caller's business; these are the KEYS. */
export function describeValue(
  value: FilterValue | undefined,
  labelOf?: (key: string) => string,
): string {
  if (!value) return '';
  switch (value.kind) {
    case 'list':
      return value.values.map((v) => labelOf?.(v) ?? v).join(', ');
    case 'text':
      return value.value;
    case 'number':
      return String(value.value);
    case 'numberRange':
      // An open end is a SENTENCE, not a missing half. "8 –" reads as output
      // that got cut off, which is how a deliberately open-ended range came to
      // look like a bug on the chip and in the export's About sheet.
      if (value.min !== null && value.max !== null) return `${value.min} – ${value.max}`;
      if (value.min !== null) return `${value.min} or more`;
      return value.max !== null ? `up to ${value.max}` : '';
    case 'date':
      return value.value;
    case 'dateRange':
      if (value.from !== null && value.to !== null) return `${value.from} – ${value.to}`;
      if (value.from !== null) return `from ${value.from}`;
      return value.to !== null ? `until ${value.to}` : '';
    case 'macro':
      return macroLabel(value.preset);
    case 'boolean':
      return value.value ? 'Yes' : 'No';
  }
}

/** `Customer is Acme, Globex` — the operator in the words its column reads it in. */
export function describeRule(
  model: ReportModel,
  rule: FilterRule,
  labelOf?: (field: string, key: string) => string,
): string {
  const label = fieldLabel(model, rule.field);
  const value = describeValue(rule.value, labelOf ? (key) => labelOf(rule.field, key) : undefined);
  const phrase = `${label} ${opLabel(rule.op, filterValueType(model, rule.field))}`;
  return value ? `${phrase} ${value}` : phrase;
}

/** A rule, or a group in brackets — the sentence the bar writes. */
export function describeFilter(
  model: ReportModel,
  node: FilterNode,
  labelOf?: (field: string, key: string) => string,
): string {
  if (!isFilterGroup(node)) return describeRule(model, node, labelOf);
  const inner = node.children.map((child) => describeRule(model, child, labelOf));
  return `(${inner.join(` ${node.join} `)})`;
}

/**
 * Every filter, one sentence each, paused ones marked.
 *
 * A paused filter is ON the report and not applied, and a list that simply left
 * it out would be the one place that fact disappears — an exported workbook is
 * read by people who never saw the bar.
 */
export function describeFilters(
  model: ReportModel,
  nodes: readonly FilterNode[],
  labelOf?: (field: string, key: string) => string,
): string[] {
  return nodes.map((node) => {
    const text = describeFilter(model, node, labelOf);
    return !isFilterGroup(node) && node.paused ? `${text} (paused — not applied)` : text;
  });
}
